/**
 * SortableTh — drop-in `<th>` that wires to a useSortable result.
 *
 * Visually matches the TNSC SortableTh: arrow glyph (▲ / ▼ / ↕), hover
 * tint, aria-sort attribute for screen readers.
 */

import React from 'react';

import type { UseSortableResult } from '../hooks/useSortable';

interface Props<T> {
	field: string;
	sort: UseSortableResult<T>;
	numeric?: boolean;
	style?: React.CSSProperties;
	title?: string;
	children: React.ReactNode;
}

export default function SortableTh<T>({
	field,
	sort,
	numeric = false,
	style,
	title,
	children,
}: Props<T>) {
	const { sortBy, sortDir, toggle } = sort;
	const active = sortBy === field;
	const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
	return (
		<th
			scope="col"
			onClick={() => toggle(field)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					toggle(field);
				}
			}}
			tabIndex={0}
			className={numeric ? 'num sortable' : 'sortable'}
			aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
			title={title ?? `Sort by ${typeof children === 'string' ? children : field}`}
			style={{
				cursor: 'pointer',
				userSelect: 'none',
				whiteSpace: 'nowrap',
				...style,
			}}
		>
			<span
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 4,
					justifyContent: numeric ? 'flex-end' : 'flex-start',
					width: '100%',
				}}
			>
				{children}
				<span
					aria-hidden="true"
					style={{
						fontSize: 9,
						lineHeight: 1,
						color: active ? 'var(--gold-600)' : 'var(--text-faint)',
						opacity: active ? 1 : 0.55,
					}}
				>
					{arrow}
				</span>
			</span>
		</th>
	);
}
